export function getDefaultDateRange() {
  const today = new Date();
  const start = new Date(today);
  start.setMonth(today.getMonth() - 12);
  return { start, end: today };
}

export function isValidDateRange(start, end) {
  if (!start || !end) return false;
  return start <= end;
}

export function fillMissingMonths(data, start, end) {
  if (!start || !end) return [];

  const months = [];
  const cursor = new Date(start);
  const endDate = new Date(end);

  while (cursor <= endDate) {
    months.push(cursor.toISOString().slice(0, 7));
    cursor.setMonth(cursor.getMonth() + 1);
  }

  const filteredData = (data || []).filter((item) => item.month !== 'Unknown');
  return months.map((month) => {
    const found = filteredData.find((item) => item.month === month);
    return found || { month, failureRate: '0.00' };
  });
}

export function buildFailureRateChartData(filledFailureRateData) {
  return {
    labels: filledFailureRateData.map((item) => {
      const [year, month] = item.month.split('-');
      const date = new Date(year, month - 1);
      return date.toLocaleString('default', { month: 'short', year: 'numeric' });
    }),
    datasets: [
      {
        label: 'Failure Rate (%)',
        data: filledFailureRateData.map((item) => parseFloat(item.failureRate)),
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        fill: true,
        tension: 0.3,
      },
    ],
  };
}

export function buildPieChartData(pieChartData) {
  return {
    labels: ['Pass (%)', 'Fail (%)'],
    datasets: [
      {
        data: [
          Number(pieChartData?.PASS) || 0,
          Number(pieChartData?.FAIL) || 0,
        ],
        backgroundColor: ['#4CAF50', '#F44336'],
        hoverBackgroundColor: ['#66BB6A', '#E57373'],
        borderWidth: 1,
      },
    ],
  };
}

export function buildParetoChartData(paretoChartData) {
  const filteredParetoChartData = Array.isArray(paretoChartData)
    ? paretoChartData.filter((item) => Number(item.failCount) > 0)
    : [];

  if (!filteredParetoChartData.length) return null;

  return {
    labels: filteredParetoChartData.map((item) => [
      String(item.stepNumber || ''),
      String(item.stepName || 'No Name'),
    ]),
    fullLabels: filteredParetoChartData.map((item) =>
      `Step ${item.stepNumber || ''} - ${item.stepName || 'No Name'}`
    ),
    datasets: [
      {
        label: 'Fail Count',
        data: filteredParetoChartData.map((item) => Math.round(Number(item.failCount) || 0)),
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        type: 'bar',
        yAxisID: 'y',
      },
      {
        label: 'Cumulative Fail Percentage',
        data: filteredParetoChartData.map((item) => Number(item.cumulativePercentage) || 0),
        borderColor: 'rgba(54, 162, 235, 0.8)',
        backgroundColor: 'rgba(54, 162, 235, 0.8)',
        type: 'line',
        fill: false,
        tension: 0.4,
        yAxisID: 'y1',
        pointRadius: 3,
      },
    ],
  };
}

export const pieChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top',
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const label = context.label || '';
          const value = context.raw || 0;
          return `${label}: ${value}`;
        },
      },
    },
  },
};

export const paretoChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  scales: {
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: 'Fail Count',
      },
      ticks: {
        precision: 0,
        callback: (value) => Math.round(Number(value)).toString(),
      },
    },
    y1: {
      beginAtZero: true,
      position: 'right',
      grid: {
        drawOnChartArea: false,
      },
      title: {
        display: true,
        text: 'Cumulative Percentage',
      },
      ticks: {
        callback: (value) => `${Math.round(Number(value))}%`,
      },
      min: 0,
      max: 100,
    },
    x: {
      title: {
        display: true,
        text: 'Step Number / Step Name',
      },
      ticks: {
        autoSkip: true,
        maxTicksLimit: 8,
        font: { size: 10 },
        maxRotation: 50,
        minRotation: 50,
        callback: function labelCallback(value) {
          const label = this.getLabelForValue(value);
          if (!Array.isArray(label)) return label;
          return [
            label[0],
            label[1].length > 12 ? `${label[1].slice(0, 9)}...` : label[1],
          ];
        },
      },
    },
  },
  plugins: {
    legend: {
      display: true,
      position: 'top',
    },
    tooltip: {
      callbacks: {
        title: (items) => {
          const chartData = items?.[0]?.chart?.data;
          const index = items?.[0]?.dataIndex;
          return chartData?.fullLabels?.[index] || '';
        },
        label: (context) => {
          const label = context.dataset.label || '';
          const value = context.raw;
          if (context.dataset.yAxisID === 'y1') {
            return `${label}: ${Number(value).toFixed(2)}%`;
          }
          return `${label}: ${Math.round(Number(value) || 0)}`;
        },
      },
    },
  },
};

export const failureRateChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (context) => `Failure Rate: ${context.raw.toFixed(2)}%`,
      },
    },
  },
  scales: {
    x: {
      title: {
        display: true,
        text: 'Month',
        font: { size: 14 },
      },
      ticks: {
        autoSkip: true,
        maxRotation: 0,
        minRotation: 0,
      },
    },
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: 'Failure Rate (%)',
        font: { size: 14 },
      },
      ticks: {
        callback: (value) => `${value}%`,
      },
    },
  },
};
